import { Flex } from "@redotech/redo-web/flex";
import * as phoneWidgetCss from "@redotech/redo-web/voice/phone-widget.module.css";
import { usePhoneWidget } from "@redotech/redo-web/voice/shared/phone-widget-context";
import { PhoneWidgetHeader } from "@redotech/redo-web/voice/shared/phone-widget-header";
import { memo, useContext } from "react";
import { CollapsedPhoneWidgetV2 } from "./collapsed-phone-widget-v2";
import { VoiceContextV2 } from "./voice-context-v2";
import { VoiceUserPanelInner } from "./voice-user-panel-inner";

interface PhoneWidgetV2Props {
  readonly merchantPhoneNumber: string;
}

/**
 * Floating chrome wrapper for the v2 voice widget. Mirrors v1's
 * `phone-widget.tsx`: position-fixed pill ↔ expanded panel via
 * `usePhoneWidget()` visibility/expand state. Wraps a single
 * `VoiceUserPanelInner` (the actual call UI), with `showImpersonation`
 * always false in production.
 *
 * Uses `CollapsedPhoneWidgetV2` for the pill — a v2-aware version that
 * reads from `VoiceContextV2` so accept/reject buttons appear on
 * incoming calls and the hangup button appears on active calls without
 * requiring the operator to expand the widget first.
 */
export const PhoneWidgetV2 = memo(function PhoneWidgetV2({
  merchantPhoneNumber,
}: PhoneWidgetV2Props) {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }

  const { isVisible, hide } = usePhoneWidget();

  if (!isVisible) {
    return null;
  }

  return (
    <Flex className={phoneWidgetCss.widgetContainer} dir="column">
      {!ctx.widgetExpanded && (
        <CollapsedPhoneWidgetV2
          onClose={hide}
          onExpand={() => ctx.setWidgetExpanded(true)}
        />
      )}
      {ctx.widgetExpanded && (
        <Flex className={phoneWidgetCss.bodySections} dir="column" gap="none">
          <PhoneWidgetHeader
            merchantPhoneNumber={merchantPhoneNumber}
            onClose={hide}
            setExpandedState={(state) =>
              ctx.setWidgetExpanded(state === "full")
            }
          />
          <VoiceUserPanelInner />
        </Flex>
      )}
    </Flex>
  );
});
